import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { DocumentType } from '@backrs_ui/grow-ui/src/models/models';

export type DocumentExtension = 'pdf' | 'docx' | 'txt' | 'image';

type UploadDocumentParams = Readonly<{
  documentType: DocumentType;
  existingDocuments?: { filename: string; url: string }[];
  coachingUser?: { id: string; name?: string };
}>;

type MimeTypeConfig = Readonly<{
  type: DocumentExtension;
  extension: string;
  mimeTypes: string[];
}>;

const { fileUpload } = appStrings.modules.chatBox;

export const getCoachingUserName = (coachingUser?: { id: string; name?: string }) => {
  if (!coachingUser) {
    return undefined;
  }

  return coachingUser?.name?.split(' ')[0];
};

export const getUploadDocumentMessage = ({
  documentType,
  existingDocuments,
  coachingUser,
}: UploadDocumentParams) => {
  const hasExistingDocuments = existingDocuments && existingDocuments.length > 0;

  switch (documentType) {
    case 'resume':
      if (hasExistingDocuments) {
        return fileUpload.uploadOrConfirmResumeMultiple(
          existingDocuments,
          getCoachingUserName(coachingUser),
        );
      }

      return fileUpload.uploadResume(getCoachingUserName(coachingUser));
    case 'transcript':
      if (hasExistingDocuments) {
        return fileUpload.uploadOrConfirmTranscriptMultiple(
          existingDocuments,
          getCoachingUserName(coachingUser),
        );
      }

      return fileUpload.uploadTranscript(getCoachingUserName(coachingUser));

    case 'personal_assessment':
      if (hasExistingDocuments) {
        return fileUpload.uploadOrConfirmPersonalAssessmentMultiple(
          existingDocuments,
          getCoachingUserName(coachingUser),
        );
      }

      return fileUpload.uploadYouPersonalAssessment(getCoachingUserName(coachingUser));
    default:
      return '';
  }
};

export const FILE_CONFIG: Record<DocumentExtension, MimeTypeConfig> = {
  pdf: {
    type: 'pdf',
    extension: '.pdf',
    mimeTypes: ['application/pdf'],
  },
  docx: {
    type: 'docx',
    extension: '.docx',
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  txt: {
    type: 'txt',
    extension: '.txt',
    mimeTypes: ['text/plain'],
  },
  image: {
    type: 'image',
    extension: '.jpg,.jpeg,.png',
    mimeTypes: ['image/jpeg', 'image/png'],
  },
} as const;

export const getFileType = (mimeType: string): DocumentExtension => {
  return (
    Object.values(FILE_CONFIG).find((config) => config.mimeTypes.includes(mimeType))?.type ?? 'txt'
  );
};

export type FileExtension = 'jpg' | 'png' | 'pdf' | 'docx' | 'txt';

type KnownMimeType =
  | 'image/jpeg'
  | 'image/png'
  | 'application/pdf'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  | 'text/plain';

const MIME_TYPE_TO_EXTENSION: Record<KnownMimeType, FileExtension> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'text/plain': 'txt',
};

export const getFileExtensionFromMimeType = (mimeType: string): FileExtension | undefined =>
  MIME_TYPE_TO_EXTENSION[mimeType as KnownMimeType];
